class LogoShader extends HTMLElement {
  constructor() {
    super();
    this.canvas = null;
    this.renderer = null;
    this.scene = null;
    this.postScene = null;
    this.camera = null;
    this.postCamera = null;
    this.mesh = null;
    this.clock = null;
    this.imageAspect = 1;
    this.introDuration = 1.8;
    this.mouse = { x: 0.5, y: 0.5 };
    this.prevMouse = { x: 0.5, y: 0.5 };
    this.fluidSize = 128;
    this.isPressed = false;
    this.resizeObserver = null;
  }

  connectedCallback() {
    this.init().catch((error) => console.warn('Shader unavailable; showing original media.', error));
    this.events = new AbortController();
    this.resizeObserver = new ResizeObserver(() => this.onResize());
    this.resizeObserver.observe(this);
    const options = { signal: this.events.signal };
    window.addEventListener('mousemove', (e) => this.onMouseMove(e), options);
    window.addEventListener('touchmove', (e) => this.onTouchMove(e), options);
    window.addEventListener('mousedown', () => this.onPress(true), options);
    window.addEventListener('mouseup', () => this.onPress(false), options);
    window.addEventListener('touchstart', () => this.onPress(true), options);
    window.addEventListener('touchend', () => this.onPress(false), options);
  }

  disconnectedCallback() {
    this.events?.abort();
    cancelAnimationFrame(this.animationFrame);
    this.material?.uniforms.uTexture.value.dispose();
    if (this.renderer) {
      this.renderer.dispose();
      this.renderer = null;
    }
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
  }

  async init() {
    const THREE = await this.loadThree();
    if (!this.isConnected) return;
    this.THREE = THREE;
    this.clock = new THREE.Clock();
    this.canvas = this.querySelector('canvas');
    this.media = this.querySelector('video, img');
    this.video = this.querySelector('video');
    this.strobeText = !this.video && this.hasAttribute('data-strobe-text');
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.ticket = (this.video || this.strobeText) ? this.closest('.teaser')?.querySelector('.teaser__cta--tickets') : null;

    if (this.video) {
      this.video.addEventListener('seeked', () => { this.lastFrameTime = undefined; }, { signal: this.events.signal });
      if (this.video.readyState < 2) {
        await new Promise((resolve, reject) => {
          this.video.addEventListener('loadeddata', resolve, { once: true });
          this.video.addEventListener('error', reject, { once: true });
        });
      }
      if (!this.isConnected) return;
      if (this.ticket) {
        const sampler = document.createElement('canvas');
        sampler.width = 32;
        sampler.height = 40;
        this.frameContext = sampler.getContext('2d', { willReadFrequently: true });
      }
      this.setupScene(THREE, new THREE.VideoTexture(this.video), this.video);
    } else {
      new THREE.TextureLoader().load(this.media.src, (tex) => {
        if (!this.isConnected) return tex.dispose();
        this.setupScene(THREE, tex, this.media);
      });
    }
  }

  setupScene(THREE, texture, img) {
    const imgWidth = img.videoWidth || img.naturalWidth || img.width || 500;
    const imgHeight = img.videoHeight || img.naturalHeight || img.height || 500;
    this.imageAspect = imgWidth / imgHeight;
    
    const containerWidth = this.offsetWidth || 500;
    const width = containerWidth;
    const height = img.clientHeight || containerWidth / this.imageAspect;

    this.style.position = 'relative';
    Object.assign(this.canvas.style, {
      position: 'absolute', inset: '0', pointerEvents: 'none'
    });

    this.renderer = new THREE.WebGLRenderer({ 
      canvas: this.canvas, 
      alpha: true,
      antialias: false
    });
    this.renderer.setSize(width, height);
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    this.renderer.setClearColor(0x000000, 1);

    const pixelRatio = this.renderer.getPixelRatio();
    this.renderTarget = new THREE.WebGLRenderTarget(width * pixelRatio, height * pixelRatio);
    
    const fluidOptions = {
      format: THREE.RGBAFormat,
      type: THREE.FloatType,
      minFilter: THREE.LinearFilter,
      magFilter: THREE.LinearFilter
    };
    this.fluidA = new THREE.WebGLRenderTarget(this.fluidSize, this.fluidSize, fluidOptions);
    this.fluidB = new THREE.WebGLRenderTarget(this.fluidSize, this.fluidSize, fluidOptions);

    this.scene = new THREE.Scene();
    this.postScene = new THREE.Scene();
    this.fluidScene = new THREE.Scene();
    
    this.camera = new THREE.OrthographicCamera(-0.5, 0.5, 0.5, -0.5, 0.1, 10);
    this.camera.position.z = 1;
    
    this.postCamera = new THREE.OrthographicCamera(-0.5, 0.5, 0.5, -0.5, 0.1, 10);
    this.postCamera.position.z = 1;

    const geometry = new THREE.PlaneGeometry(1, 1);
    
    this.material = new THREE.ShaderMaterial({
      uniforms: {
        uTexture: { value: texture },
        uStrobe: { value: 0 }
      },
      vertexShader: `
        varying vec2 vUv;
        void main() {
          vUv = uv;
          gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
        }
      `,
      fragmentShader: `
        uniform sampler2D uTexture;
        uniform float uStrobe;
        varying vec2 vUv;
        void main() {
          vec4 color = texture2D(uTexture, vUv);
          // ponytail: bands match the SUNDOWN 002 artwork; use an explicit mask for other layouts.
          float headline = step(0.21, vUv.y) - step(0.40, vUv.y);
          float venue = step(0.03, vUv.y) - step(0.15, vUv.y);
          color.gb = mix(color.gb, vec2(color.r), uStrobe * (headline + venue));
          gl_FragColor = color;
        }
      `,
      transparent: true
    });

    this.mesh = new THREE.Mesh(geometry, this.material);
    this.scene.add(this.mesh);

    const fluidGeometry = new THREE.PlaneGeometry(1, 1);
    
    this.fluidMaterial = new THREE.ShaderMaterial({
      uniforms: {
        uFluid: { value: null },
        uMouse: { value: new THREE.Vector2(0.5, 0.5) },
        uMouseVel: { value: new THREE.Vector2(0, 0) },
        uDissipation: { value: 0.97 }
      },
      vertexShader: `
        varying vec2 vUv;
        void main() {
          vUv = uv;
          gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
        }
      `,
      fragmentShader: `
        uniform sampler2D uFluid;
        uniform vec2 uMouse;
        uniform vec2 uMouseVel;
        uniform float uDissipation;
        varying vec2 vUv;
        
        void main() {
          vec2 texel = 1.0 / vec2(128.0);
          
          vec2 vel = texture2D(uFluid, vUv).xy;
          vec2 prevPos = vUv - vel * texel * 1.5;
          vec4 advected = texture2D(uFluid, prevPos);
          
          vec2 diff = vUv - uMouse;
          float dist = length(diff);
          float radius = 0.08;
          float strength = smoothstep(radius, 0.0, dist);
          
          vec2 force = uMouseVel * strength * 2.0;
          
          vec2 newVel = advected.xy * uDissipation + force;
          
          gl_FragColor = vec4(newVel, 0.0, 1.0);
        }
      `
    });
    
    this.fluidQuad = new THREE.Mesh(fluidGeometry.clone(), this.fluidMaterial);
    this.fluidScene.add(this.fluidQuad);

    const postGeometry = new THREE.PlaneGeometry(1, 1);
    
    this.postMaterial = new THREE.ShaderMaterial({
      uniforms: {
        uTexture: { value: null },
        uFluid: { value: null },
        uTime: { value: 0 },
        uIntro: { value: 0 },
        uPressed: { value: 0 },
        uResolution: { value: new THREE.Vector2(width, height) }
      },
      vertexShader: `
        varying vec2 vUv;
        void main() {
          vUv = uv;
          gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
        }
      `,
      fragmentShader: `
        uniform sampler2D uTexture;
        uniform sampler2D uFluid;
        uniform float uTime;
        uniform float uIntro;
        uniform float uPressed;
        uniform vec2 uResolution;
        varying vec2 vUv;

        float random(vec2 st) {
          return fract(sin(dot(st.xy, vec2(12.9898, 78.233))) * 43758.5453123);
        }

        void main() {
          vec2 uv = vUv;
          float introInv = 1.0 - uIntro;
          
          float pixelSize = 10.0;
          vec2 pixelUv = floor(uv * uResolution / pixelSize) * pixelSize / uResolution;
          
          vec2 fluidVel = texture2D(uFluid, pixelUv).xy;
          vec2 distortion = fluidVel * 0.15;
          
          float revealProgress = uIntro;
          
          float scanLineGlow = 0.0;
          for (float i = 0.0; i < 3.0; i++) {
            float scanY = revealProgress * 1.2 - i * 0.06;
            float scanDist = abs(uv.y - scanY);
            scanLineGlow += smoothstep(0.02, 0.0, scanDist) * (1.0 - i * 0.3);
          }
          scanLineGlow *= introInv;
          
          float jitter = sin(floor(uv.y * 40.0) * 3.0 + uTime * 10.0) * 0.005 * introInv;
          
          float scanY1 = 1.0 - fract(uTime * 0.12);
          float scanY2 = 1.0 - fract(uTime * 0.25 + 0.5);
          
          float scanDist1 = abs(uv.y - scanY1);
          float scanDist2 = abs(uv.y - scanY2);
          
          float scanShift1 = smoothstep(0.06, 0.0, scanDist1) * 0.025;
          float scanShift2 = smoothstep(0.04, 0.0, scanDist2) * 0.015;
          
          float totalScanShift = scanShift1 + scanShift2;
          
          vec2 sampleUv = uv + distortion + vec2(jitter + totalScanShift, 0.0);
          
          float aberration = 0.003 + 0.01 * introInv + length(distortion) * 2.0;
          float r = texture2D(uTexture, sampleUv + vec2(aberration, 0.0)).r;
          float g = texture2D(uTexture, sampleUv).g;
          float b = texture2D(uTexture, sampleUv - vec2(aberration, 0.0)).b;
          float a = texture2D(uTexture, sampleUv).a;
          
          vec3 color = vec3(r, g, b);
          
          float scanline = sin(uv.y * uResolution.y * 1.5) * 0.03 * uIntro;
          color -= scanline * color;
          
          color += scanLineGlow * vec3(1.0, 0.3, 0.2) * 0.35;
          
          float movingScanY = 1.0 - fract(uTime * 0.12);
          float movingScanDist = abs(uv.y - movingScanY);
          float movingScanGlow = smoothstep(0.025, 0.0, movingScanDist) * 0.08 * uIntro;
          color += movingScanGlow * vec3(1.0, 0.2, 0.15);
          
          float noise = (random(uv * uResolution + uTime * 30.0) - 0.5) * 0.02;
          color += noise;
          
          vec2 centered = uv - 0.5;
          float dist = length(centered);
          float vignette = 1.0 - dist * 0.35;
          color *= vignette;
          
          color.r *= 1.05;
          
          if (uPressed > 0.5) {
            float brightness = dot(color, vec3(0.299, 0.587, 0.114));
            vec3 invertedColor = vec3(0.0);
            invertedColor.r = 1.0 - brightness;
            invertedColor.g = color.r * 0.1;
            invertedColor.b = color.r * 0.1;
            float redAmount = color.r - max(color.g, color.b);
            invertedColor *= (1.0 - redAmount * 0.8);
            color = invertedColor;
          }
          
          gl_FragColor = vec4(color, a);
        }
      `,
      transparent: true
    });

    this.postQuad = new THREE.Mesh(postGeometry, this.postMaterial);
    this.postScene.add(this.postQuad);

    this.onResize();
    this.canvas.style.display = 'block';
    this.animate();
  }

  async loadThree() {
    if (window.THREE) return window.THREE;
    
    return window.sundownThreePromise ||= new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = 'https://cdnjs.cloudflare.com/ajax/libs/three.js/r128/three.min.js';
      script.onload = () => resolve(window.THREE);
      script.onerror = reject;
      document.head.appendChild(script);
    });
  }

  animate() {
    if (!this.renderer) return;
    
    this.animationFrame = requestAnimationFrame(() => this.animate());
    this.syncTicketColor();
    
    const elapsed = this.clock.getElapsedTime();
    if (this.strobeText) {
      const white = !this.reducedMotion.matches && Math.floor(elapsed * 6) % 2 === 1;
      this.material.uniforms.uStrobe.value = Number(white);
      this.ticket?.classList.toggle('is-white', white);
    }
    const intro = Math.min(elapsed / this.introDuration, 1.0);
    const easedIntro = 1.0 - Math.pow(1.0 - intro, 3);
    
    const mouseVelX = (this.mouse.x - this.prevMouse.x) * 5;
    const mouseVelY = (this.mouse.y - this.prevMouse.y) * 5;
    this.prevMouse.x += (this.mouse.x - this.prevMouse.x) * 0.5;
    this.prevMouse.y += (this.mouse.y - this.prevMouse.y) * 0.5;
    
    this.fluidMaterial.uniforms.uFluid.value = this.fluidA.texture;
    this.fluidMaterial.uniforms.uMouse.value.set(this.mouse.x, this.mouse.y);
    this.fluidMaterial.uniforms.uMouseVel.value.set(mouseVelX, mouseVelY);
    
    this.renderer.setRenderTarget(this.fluidB);
    this.renderer.render(this.fluidScene, this.postCamera);
    
    const temp = this.fluidA;
    this.fluidA = this.fluidB;
    this.fluidB = temp;
    
    this.postMaterial.uniforms.uTime.value = elapsed;
    this.postMaterial.uniforms.uIntro.value = easedIntro;
    this.postMaterial.uniforms.uFluid.value = this.fluidA.texture;
    
    this.renderer.setRenderTarget(this.renderTarget);
    this.renderer.render(this.scene, this.camera);
    
    this.postMaterial.uniforms.uTexture.value = this.renderTarget.texture;
    this.renderer.setRenderTarget(null);
    this.renderer.render(this.postScene, this.postCamera);
  }

  syncTicketColor() {
    if (!this.frameContext || this.video.seeking || this.video.readyState < 2 || this.lastFrameTime === this.video.currentTime) return;
    this.lastFrameTime = this.video.currentTime;
    try {
      this.frameContext.drawImage(this.video, 0, 0, 32, 40);
      const pixels = this.frameContext.getImageData(0, 0, 32, 40).data;
      let white = 0;
      for (let i = 0; i < pixels.length; i += 4) {
        if (Math.min(pixels[i], pixels[i + 1], pixels[i + 2]) > 160) white++;
      }
      // ponytail: detect white lettering, not arbitrary palettes; retune if the art changes.
      this.ticket.classList.toggle('is-white', white / (32 * 40) > 0.035);
    } catch {
      // A video without CORS access must not break playback or leave the button flashing.
      this.ticket.classList.remove('is-white');
      this.frameContext = null;
    }
  }

  onResize() {
    if (!this.renderer || !this.mesh) return;
    
    const width = this.offsetWidth || 500;
    const height = this.media.clientHeight || width / this.imageAspect;
    const aspect = width / height;
    this.mesh.scale.set(Math.min(this.imageAspect / aspect, 1), Math.min(aspect / this.imageAspect, 1), 1);
    this.renderer.setSize(width, height);
    
    const pixelRatio = this.renderer.getPixelRatio();
    this.renderTarget.setSize(width * pixelRatio, height * pixelRatio);
    
    this.postMaterial.uniforms.uResolution.value.set(width, height);
  }

  onMouseMove(e) {
    const rect = this.getBoundingClientRect();
    this.mouse.x = (e.clientX - rect.left) / rect.width;
    this.mouse.y = 1.0 - (e.clientY - rect.top) / rect.height;
  }
  
  onTouchMove(e) {
    if (e.touches.length > 0) {
      const rect = this.getBoundingClientRect();
      this.mouse.x = (e.touches[0].clientX - rect.left) / rect.width;
      this.mouse.y = 1.0 - (e.touches[0].clientY - rect.top) / rect.height;
    }
  }
  
  onPress(pressed) {
    this.isPressed = pressed;
    if (this.postMaterial) {
      this.postMaterial.uniforms.uPressed.value = pressed ? 1.0 : 0.0;
    }
    document.body.classList.toggle('inverted', pressed);
  }
}

customElements.define('logo-shader', LogoShader);
